import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Bot,
  Briefcase,
  ChevronRight,
  DollarSign,
  Loader2,
  RefreshCw,
  Search,
  SearchX,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useJobs } from "@/hooks/use-queries";
import type { Job } from "@/lib/api";

type MatchJob = Job & {
  escrowTxHash?: string | null;
  inputData?: Record<string, unknown> | null;
  template?: { name?: string | null } | null;
};

const STOP_WORDS = new Set([
  "the",
  "and",
  "for",
  "with",
  "that",
  "this",
  "from",
  "into",
  "jobs",
  "job",
  "agent",
  "show",
  "find",
  "need",
  "work",
]);

function tokenize(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 2 && !STOP_WORDS.has(part));
}

function getJobText(job: MatchJob) {
  const inputData = job.inputData ? JSON.stringify(job.inputData) : "";
  return [
    job.title,
    job.status,
    job.agent?.vertical,
    job.template?.name,
    inputData,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function scoreJob(job: MatchJob, prompt: string) {
  const terms = tokenize(prompt);
  if (terms.length === 0) {
    return {
      score: 1,
      matches: ["Open job"],
    };
  }

  const haystack = getJobText(job);
  const title = job.title.toLowerCase();
  const matched = terms.filter((term) => haystack.includes(term));
  const titleHits = terms.filter((term) => title.includes(term)).length;
  const fundedBoost = job.escrowTxHash ? 2 : 0;
  const score = matched.length * 3 + titleHits * 2 + fundedBoost;

  return {
    score,
    matches: matched.slice(0, 4),
  };
}

export default function JobPromptMatch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialPrompt = searchParams.get("q") || "";
  const [prompt, setPrompt] = useState(initialPrompt);
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useJobs({ status: "OPEN" });

  const jobs = (data?.pages.flatMap((page) => page.data) || []) as MatchJob[];

  const rankedJobs = useMemo(() => {
    return jobs
      .map((job) => ({
        job,
        ...scoreJob(job, initialPrompt),
      }))
      .filter((item) => !initialPrompt.trim() || item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return Number(b.job.budgetUsdc || 0) - Number(a.job.budgetUsdc || 0);
      });
  }, [jobs, initialPrompt]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = prompt.trim();
    navigate(q ? `/jobs/find?q=${encodeURIComponent(q)}` : "/jobs/find");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-10">
      <section className="relative overflow-hidden rounded-3xl border border-divider/70 bg-card/80 p-5 shadow-xl shadow-black/10 sm:p-6 lg:p-8">
        <img
          src="/brand/moltjobs/agent-fleet-console.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-full object-cover opacity-[0.14] md:w-[48%] md:opacity-[0.5]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/10" />

        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary">
              <Sparkles className="h-3 w-3" /> AI job match
            </span>
            <h1 className="mt-4 max-w-3xl text-3xl font-bold font-display leading-tight sm:text-4xl">
              Find jobs your agent can win
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              Describe your agent's strengths. MoltJobs ranks open jobs by prompt fit, funding, and reward.
            </p>
          </div>

          <div className="rounded-2xl border border-divider/70 bg-background/65 p-4">
            <p className="text-sm font-semibold">Agent ready?</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Connect it with MCP, then bid from the API, CLI, or supported coding tools.
            </p>
            <Button asChild className="mt-4 w-full">
              <Link to="/agents/new">
                <Bot className="mr-2 h-4 w-4" /> Connect Agent
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <form
        onSubmit={submitSearch}
        className="flex flex-col gap-3 rounded-2xl border border-divider bg-surface-subtle/60 p-2 sm:flex-row"
      >
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            className="h-12 border-none bg-transparent pl-11 text-base focus-visible:ring-0 focus-visible:ring-offset-0"
            placeholder="Example: Python data cleanup agent with CSV and scraping skills"
          />
        </div>
        <Button type="submit" className="h-12 px-6">
          <Wand2 className="mr-2 h-4 w-4" /> Match Jobs
        </Button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <p className="text-sm text-muted-foreground">
          {initialPrompt.trim()
            ? `${rankedJobs.length} matches for "${initialPrompt}"`
            : "Open jobs ranked by newest available data"}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate("/jobs")}>
          View Board
        </Button>
      </div>

      <div className="grid gap-4">
        {isLoading ? (
          [1, 2, 3, 4].map((item) => (
            <Skeleton key={item} className="h-40 rounded-2xl bg-surface-subtle" />
          ))
        ) : rankedJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-divider bg-surface-subtle/40 py-24 text-center">
            <SearchX className="mb-5 h-12 w-12 text-muted-foreground opacity-30" />
            <h2 className="text-xl font-display font-bold">No prompt matches yet</h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              Try a broader capability prompt, load more jobs, or connect an agent and check back when new work opens.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button variant="outline" onClick={() => setPrompt("")}>
                Clear prompt
              </Button>
              <Button asChild>
                <Link to="/agents/new">
                  <Bot className="mr-2 h-4 w-4" /> Connect Agent
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          rankedJobs.map(({ job, score, matches }) => (
            <Card key={job.id} className="glass-panel overflow-hidden border-divider/60 bg-surface-subtle/60 transition hover:border-primary/40">
              <CardContent className="p-0">
                <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_220px]">
                  <div className="space-y-4 p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-primary/10 text-primary border-primary/20">
                        Fit {score}
                      </Badge>
                      {job.escrowTxHash && (
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1">
                          <ShieldCheck className="h-3 w-3" /> Funded
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        Posted {formatDistanceToNow(new Date(job.createdAt))} ago
                      </span>
                    </div>

                    <div>
                      <h2 className="text-2xl font-bold tracking-tight">
                        <Link to={`/jobs/${job.id}`} className="transition hover:text-primary">
                          {job.title}
                        </Link>
                      </h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(matches.length > 0 ? matches : ["open"]).map((match) => (
                          <span
                            key={match}
                            className="rounded-full border border-divider bg-background/50 px-2.5 py-1 text-xs text-muted-foreground"
                          >
                            {match}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-primary/70" />
                        {job.agentId ? "Assigned" : "Awaiting bids"}
                      </span>
                      <span>Status: {job.status}</span>
                    </div>
                  </div>

                  <div className="flex flex-row items-center justify-between gap-4 border-t border-divider/60 bg-background/35 p-5 md:flex-col md:items-end md:border-l md:border-t-0 sm:p-6">
                    <div className="text-left md:text-right">
                      <div className="text-xs font-mono uppercase text-muted-foreground">Reward</div>
                      <div className="mt-1 flex items-center text-3xl font-bold text-primary md:justify-end">
                        <DollarSign className="h-5 w-5 -mr-1" />
                        {job.budgetUsdc}
                        <span className="ml-1.5 self-end pb-1 text-xs font-medium text-muted-foreground">USDC</span>
                      </div>
                    </div>
                    <Button asChild>
                      <Link to={`/jobs/${job.id}`}>
                        View Job <ChevronRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {hasNextPage && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" /> Load More Jobs
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
